import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, Phone, X, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { getPostBySlug } from "@/lib/blog";
import { resolveBlogImage } from "@/lib/blog-images";
import { CategoryChip } from "@/components/CategoryChip";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";

interface ArticleReaderContextValue {
  openArticle: (slug: string) => void;
  closeArticle: () => void;
}

const ArticleReaderContext = createContext<ArticleReaderContextValue | null>(null);

export function useArticleReader() {
  const ctx = useContext(ArticleReaderContext);
  if (!ctx) throw new Error("useArticleReader must be used within ArticleReaderProvider");
  return ctx;
}

export function ArticleReaderProvider({ children }: { children: React.ReactNode }) {
  const [slug, setSlug] = useState<string | null>(null);

  const openArticle = useCallback((s: string) => setSlug(s), []);
  const closeArticle = useCallback(() => setSlug(null), []);

  const value = useMemo(() => ({ openArticle, closeArticle }), [openArticle, closeArticle]);

  return (
    <ArticleReaderContext.Provider value={value}>
      {children}
      <ArticleReaderDialog slug={slug} onOpenChange={(open) => !open && closeArticle()} />
    </ArticleReaderContext.Provider>
  );
}

function ArticleReaderDialog({
  slug,
  onOpenChange,
}: {
  slug: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { lang, t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["blog", "post", slug],
    queryFn: () => getPostBySlug(slug as string),
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });

  const post = data?.post ?? null;
  const title = post ? (lang === "es" ? post.title_es : post.title_en) : "";
  const excerpt = post ? (lang === "es" ? post.excerpt_es : post.excerpt_en) : "";
  const content = post ? (lang === "es" ? post.content_es : post.content_en) : "";
  const date = post
    ? new Date(post.published_at).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <Dialog open={!!slug} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl gap-0 overflow-hidden rounded-2xl p-0 sm:w-full"
      >
        {isLoading || !post ? (
          <div className="flex min-h-[40vh] items-center justify-center p-10 text-sm text-muted-foreground">
            {isLoading ? t("blog.loading") : t("blog.empty")}
          </div>
        ) : (
          <div className="max-h-[92vh] overflow-y-auto">
            <DialogTitle className="sr-only">{title}</DialogTitle>
            <DialogDescription className="sr-only">{excerpt}</DialogDescription>

            {/* Cover */}
            <div className="relative isolate h-52 w-full overflow-hidden sm:h-72">
              <img
                src={resolveBlogImage(post.cover_image_url)}
                alt={title}
                width={1920}
                height={1080}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[oklch(0.14_0.03_50/0.82)] via-[oklch(0.14_0.03_50/0.44)] to-[oklch(0.14_0.03_50/0.10)]" />

              <DialogClose className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors hover:bg-black/65 focus:outline-none focus:ring-2 focus:ring-white/60 sm:right-5 sm:top-5">
                <X className="h-5 w-5" strokeWidth={2.5} />
                <span className="sr-only">Close article</span>
              </DialogClose>

              <div className="relative flex h-full flex-col justify-end px-5 pb-5 text-white sm:px-8 sm:pb-7">
                <CategoryChip
                  category={post.category}
                  className="self-start bg-background/90 backdrop-blur"
                />
                <h2 className="mt-3 max-w-2xl font-serif text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
                  {title}
                </h2>
                <div className="mt-3 flex items-center gap-3 text-xs text-white/90 sm:text-sm">
                  <time dateTime={post.published_at}>{date}</time>
                  <span aria-hidden>·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-4 w-4" />
                    {post.read_minutes} {t("blog.minRead")}
                  </span>
                </div>
              </div>
            </div>

            {/* Body */}
            <article className="px-5 py-8 sm:px-10 sm:py-10">
              <div className="prose-blog">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
              </div>

              {/* Hero-style CTA */}
              <div className="relative isolate mt-10 overflow-hidden rounded-2xl px-6 py-8 text-center sm:px-10 sm:py-10">
                <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[oklch(0.18_0.03_50/0.95)] via-[oklch(0.18_0.03_50/0.8)] to-[oklch(0.18_0.03_50/0.55)]" />
                <h3 className="font-serif text-xl font-bold text-white sm:text-2xl">
                  {t("blog.ctaTitle")}
                </h3>
                <p className="mx-auto mt-2 max-w-xl text-sm text-white/85">
                  {t("blog.ctaSub")}
                </p>
                <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Button
                    asChild
                    variant="gold"
                    size="xl"
                    className="hover-pulse-gold w-full sm:w-auto"
                    onClick={() => onOpenChange(false)}
                  >
                    <a href="tel:+15058680847">
                      <Phone className="h-4 w-4" /> {t("common.callPhone")}
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="whiteOutline"
                    size="xl"
                    className="w-full sm:w-auto"
                    onClick={() => onOpenChange(false)}
                  >
                    <Link to="/contact" hash="project-form">
                      {t("common.requestConsultation")} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </article>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
